import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BaseService } from "./base.service";
import { Observable } from "rxjs";
import { map, catchError, tap, switchMap } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { ICountryDescription, CountryDashboardInformation, IDropDown, IDropDownItem } from "../interfaces/idashboard";
import { IAppStore } from "../iappstore";
import { Store } from "@ngrx/store";

@Injectable()
/**
 * @whatItDoes A service for handling all country dashboard information
 *
 * @howToUse To use, inject in to any constructor
 */
export class CountryDashboardService extends BaseService {
    constructor(httpClient: HttpClient, toastr: ToastrService, store: Store<IAppStore>) {
        super(httpClient, toastr, store);
    }
    countryDashboardInformation: CountryDashboardInformation = null;

    saveDashboardContent(content: ICountryDescription): Observable<ICountryDescription> {
        return super.put("/api/countrydashboard", content)
            .pipe(map(r => r as ICountryDescription));
    }

    createDashboardContent(content: ICountryDescription): Observable<ICountryDescription> {
        return super.post("/api/countrydashboard", content)
            .pipe(map(r => r as ICountryDescription));
    }

     /**
      * This method will all relevant data for a given country and dropdown selection
      * Includes dropdown hierarchy and one record per dropdown item in the parent of the key
      * @param allDropDowns all dropdowns in dashboard hierarchy
      * @param dropDownChanged dropdown that has been updated (pass null if this is not relevant)
      * @param selectedItem selected dropdown item id (pass null if this is not relevant)
      * @param countryId country id
      * @param countryName country name
      * @param countryCode country code
      */
    getCountryDashboardData(allDropDowns: IDropDown[],
        dropDownChanged: IDropDown,
        selectedItem: IDropDownItem,
        countryId: number,
        countryName: string,
        countryCode: string): CountryDashboardInformation {
        let dropDownHierarchy: IDropDown[] = this.getDropDownHieararchy(allDropDowns, dropDownChanged, selectedItem);

        // -----Render all dropdowns APART from the key dropdown and its parent------------------- //
        let keyDropDown: IDropDown = dropDownHierarchy.filter((d: IDropDown) => d.presentationTypeId === 2)[0];// get key dropdown from list

        if (keyDropDown == null) {
            return null;
        }

        // get the parent dropdown of the key (if it exists)
        let parent: IDropDown[] = dropDownHierarchy.filter((d: IDropDown) => d.id === keyDropDown.parentDashboardDropDownId);
        let parentOfKeyDropDown: IDropDown = parent.length > 0 ? parent[0] : null;
        let countryDashboardInformation: CountryDashboardInformation = new CountryDashboardInformation();
        countryDashboardInformation.parentOfKeyDropDown = parentOfKeyDropDown;
        countryDashboardInformation.countryId = countryId;
        countryDashboardInformation.countryName = countryName;
        countryDashboardInformation.countryCode = countryCode;

        countryDashboardInformation.dropDownsToRender = dropDownHierarchy;
        return countryDashboardInformation;
    }

     /**
      * Set the country dashboard information
      * @param country dashboard information object
      * @param the selected item index
      */
    setCountryDashboardInformation(countryDashboardInformation: CountryDashboardInformation, selectedItemIndex: number): void {
        if (countryDashboardInformation != null) {
            countryDashboardInformation.selectedItemIndex = selectedItemIndex;
        }
        this.countryDashboardInformation = countryDashboardInformation;
    }

    /**
     * Retrieve the saved country dashboard information
     */
    getCountryDashboardInformation(): CountryDashboardInformation {
        return this.countryDashboardInformation;
    }

    /**
     * Get the full dropdown hierarchy for the dashboard
     */
    getDropDownHieararchy(dropDowns: IDropDown[], dropDownChanged: IDropDown, selectedItem: IDropDownItem): IDropDown[] {
        let dropDownsToRender: IDropDown[] = new Array() as IDropDown[];
        let parentDropDowns: IDropDown[] = dropDowns.filter(function (row: IDropDown): boolean {
            return row.parentDropDownItemId == null;
        });
        this.addChildDropDowns(dropDowns, parentDropDowns, dropDownChanged, selectedItem, dropDownsToRender);
        return dropDownsToRender;
    }

    private addChildDropDowns(dropDowns: IDropDown[],
        parentDropDowns: IDropDown[],
        dropdownChanged: IDropDown,
        selectedItem: IDropDownItem,
        dropDownsToRender: IDropDown[],
        dropdownItemSelected: boolean = false): void {
        for (let dropdown of parentDropDowns) {
            dropDownsToRender.push(dropdown);
            if (dropdownItemSelected) {
                dropdown.selectedDropDownItem = dropdown.dropDownItems[0];
            }

            if (dropdownChanged != null && dropdownChanged.id === dropdown.id) {
                dropdown.selectedDropDownItem = selectedItem;
                dropdownItemSelected = true;
            }
            // for first time
            if (dropdownChanged == null && selectedItem == null && dropdown.selectedDropDownItem == null) {
                dropdown.selectedDropDownItem = dropdown.dropDownItems[0];
            }
            let children: IDropDown[] = dropDowns.filter(function (row: IDropDown): boolean {
                return dropdown.selectedDropDownItem != null &&
                row.parentDropDownItemId === dropdown.selectedDropDownItem.id &&
                row.parentDashboardDropDownId === dropdown.id;
            });
            if (children.length > 0) {
                this.addChildDropDowns(dropDowns, children, dropdownChanged, selectedItem, dropDownsToRender, dropdownItemSelected);
            }
        }
    }
}



